<template>
    <div class="file-input">
        <div v-if="withLink" class="file-input__link" @click.stop>
            <input
                v-model="link"
                class="file-input__link-input"
                type="text"
                placeholder="Paste link to file"
                :required="!fileName && (context.required || required)"
            />
        </div>
        <label
            class="file-input__field"
            :class="{ 'is-active': highlight }"
            @dragenter.prevent="highlight = true"
            @dragover.prevent="highlight = true"
            @drop.prevent="onDrop"
            @dragleave.prevent="highlight = false"
        >
            <input
                :id="context.name || $attrs.name"
                ref="input"
                type="file"
                class="file-input__input"
                :name="context.name || $attrs.name"
                :multiple="multiple"
                :accept="accept"
                :required="!link && (context.required || required)"
                @change="onSelected"
            />
            <span class="file-input__label">{{ fileName ? fileName : label }}</span>
            <button
                v-if="uploadButton"
                style="display: block; margin-left: auto"
                class="button button--big"
                type="button"
                :class="{ 'is-disabled': (!fileName && !link) || (link && !isLinkValid) }"
                @click="uploadButtonClick"
            >
                Upload
            </button>
        </label>
    </div>
</template>

<script>
const acceptMap = {
    '.docx': '.docx',
    '.pdf': '.pdf',
    'image/jpeg': '.jpg',
    'image/png': '.png',
};

const noop = () => {};
const empty = () => ({});

export default {
    name: 'FileInput',

    inject: {
        context: {
            default: empty,
        },
        registerControl: {
            default: () => noop,
        },
    },
    props: {
        multiple: {
            type: Boolean,
            default: false,
        },
        label: {
            type: String,
            default: 'Нажмите, чтобы загрузить файл',
        },
        accept: {
            type: String,
            default: '*',
        },
        withLink: {
            type: Boolean,
            default: false,
        },
        required: {
            type: Boolean,
            default: false,
        },
        uploadButton: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            fileName: '',
            link: '',
            highlight: false,
            linkDebounceTimeout: null,
        };
    },
    computed: {
        acceptArray() {
            return this.accept.split(',').map((f) => f.trim());
        },
        isLinkValid() {
            if (!this.link) return false;

            return this.validateLink(this.link);
        },
    },
    watch: {
        link(value) {
            clearTimeout(this.linkDebounceTimeout);

            this.linkDebounceTimeout = setTimeout(() => {
                if (value) {
                    if (this.validateLink(value)) {
                        this.$emit('update:link', value);
                    } else {
                        this.showError();
                    }
                }
            }, 250);
        },
    },

    mounted() {
        this.registerControl(this);
    },

    methods: {
        onSelected(event) {
            const result = event.target.files;
            if (!result.length) {
                this.fileName = '';
                return;
            }

            this.selectFiles(result);
        },
        selectFiles(files) {
            if (this.multiple) {
                this.fileName = Array.from(files).reduce((prev, cur, index, array) => {
                    return prev + cur.name + `${index < array.length ? ', ' : ''}`;
                }, '');
                return this.$emit('selected', files);
            }

            this.fileName = files[0].name;
            return this.$emit('selected', files[0]);
        },
        onDrop(event) {
            this.highlight = false;
            const files = event.dataTransfer.files;
            if (!this.validateFiles(files)) {
                this.showError();
                this.$refs.input.files = null;
                return;
            }
            this.selectFiles(files);
            this.$refs.input.files = files;
        },
        validateFiles(files) {
            return Array.from(files).every((file) => {
                return this.acceptArray.some((extension) =>
                    file.name.includes(acceptMap[extension])
                );
            });
        },
        validateLink(link) {
            return this.acceptArray.some((extension) => link.includes(acceptMap[extension]));
        },
        showError() {
            this.$store.dispatch('pushToast', {
                id: Date.now(),
                type: 'error',
                title: 'Wrong file extension',
            });
        },
        focus() {
            this.highlight = true;
        },
        blur() {
            this.highlight = false;
        },
        uploadButtonClick() {
            let data = this.$refs.input.files.length ? this.$refs.input.files : this.link;

            if (!this.multiple && !this.link) {
                data = data[0];
            }

            this.$emit('upload', data);
        },
        reset() {
            this.fileName = '';
            this.link = '';
            this.$refs.input.value = '';
        },
    },
};
</script>

<style lang="scss" src="./FileInput.scss"></style>
